import React from "react";
import { Redirect, Route, Switch, useParams } from "react-router-dom";
import { ProductActivationWrapper } from "../../components/ProductActivationWrapper/ProductActivationWrapper";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { BENEFITS_BY_SLUG } from "./data/benefits";
import {
  PATH_BENEFITS_AXIS_INSURANCE,
  PATH_BENEFITS_INSURANCE_ENROLL,
  PATH_BENEFITS_LEGACY_AXIS_DETAIL,
  PATH_BENEFITS_LEGACY_PERKS_PREVIEW_DETAIL,
  PATH_BENEFITS_LEGACY_SECTION_DETAIL,
  PATH_BENEFITS_MERIDIO_ENROLL,
  PATH_BENEFITS_MERIDIO_EXPLORE_PLANS,
  PATH_BENEFITS_PERKS,
  PATH_BENEFITS_SECTION,
  PATH_BENEFITS_SECTION_TAB,
  routeHelpersBenefitDetail
} from "./paths";
import { RouteBenefitDetail } from "./routes/RouteBenefitDetail";
import { RouteBenefitsList } from "./routes/RouteBenefitsList";
import { RouteEnrollInsurance } from "./routes/RouteEnrollInsurance";
import { RouteEnrollMeridio } from "./routes/RouteEnrollMeridio";
import { RouteExploreMeridioPlans } from "./routes/RouteExploreMeridioPlans";

/**
 * Sends the pre-flattening detail URLs to their slug-only replacement.
 *
 * The legacy patterns are wider than the URLs they were meant to catch —
 * `/axis-insurance/:benefit` also matches that section's own `all` tab — so a
 * segment that names no benefit falls through to the list instead.
 */
const RedirectLegacyDetail: React.FC = () => {
  const { benefit } = useParams<{ benefit: string }>();

  if (!BENEFITS_BY_SLUG[benefit]) {
    return <RouteBenefitsList />;
  }

  return (
    <Redirect
      to={routeHelpersBenefitDetail.createLocationObject({ slug: benefit })}
    />
  );
};

export const BenefitsRoutes: React.FC = () => {
  const featureFlags = useQueryFeatureFlags();

  return (
    <ProductActivationWrapper>
      <Switch>
        {/* Meridio enrollment lives under the insurance segment, so it has to
            resolve before the legacy `:section/:tab/:benefit` redirect. */}
        {featureFlags.data?.meridioInAppEnrollment && (
          <Route
            path={PATH_BENEFITS_MERIDIO_EXPLORE_PLANS}
            exact
            component={RouteExploreMeridioPlans}
          />
        )}
        {featureFlags.data?.meridioInAppEnrollment && (
          <Route
            path={PATH_BENEFITS_MERIDIO_ENROLL}
            component={RouteEnrollMeridio}
          />
        )}

        <Route
          path={PATH_BENEFITS_INSURANCE_ENROLL}
          component={RouteEnrollInsurance}
        />

        <Route
          path={routeHelpersBenefitDetail.path}
          exact
          component={RouteBenefitDetail}
        />

        <Route
          path={PATH_BENEFITS_LEGACY_PERKS_PREVIEW_DETAIL}
          exact
          component={RedirectLegacyDetail}
        />
        <Route
          path={PATH_BENEFITS_LEGACY_AXIS_DETAIL}
          exact
          component={RedirectLegacyDetail}
        />
        <Route
          path={PATH_BENEFITS_LEGACY_SECTION_DETAIL}
          exact
          component={RedirectLegacyDetail}
        />

        <Route
          path={PATH_BENEFITS_SECTION_TAB}
          exact
          component={RouteBenefitsList}
        />
        <Route
          path={PATH_BENEFITS_SECTION}
          exact
          component={RouteBenefitsList}
        />

        <Redirect
          from="*"
          to={
            featureFlags.data?.customBenefitsLloydCapital
              ? PATH_BENEFITS_AXIS_INSURANCE
              : PATH_BENEFITS_PERKS
          }
        />
      </Switch>
    </ProductActivationWrapper>
  );
};
